import { PayloadAction } from '@reduxjs/toolkit';
import { QueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { getContext, put, takeLatest } from 'redux-saga/effects';
import { AdvancedSetting } from 'api/advanced-settings/settings-types';
import { ProjectId } from 'api/models';
import { DependenciesMap, Project, ProjectPhase } from 'api/project';
import { Sprint } from 'api/sprint-planning/types';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { StoreActionProps } from 'data/core';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import {
  updateGanttDatesAction,
  ProjectPhaseDatesActionPayload,
  updatePhaseDatesAction,
  phaseDependencyCreated,
} from 'data/projects/actions';
import { formatDate } from 'data/projects/gantt/dates';
import {
  filterTaskUpdates,
  getParentStartDate,
  getPhaseList,
  getSprintList,
  getTaskList,
  normaliseTasks,
} from 'data/projects/gantt/list-helpers';
import { Task } from 'data/tasks';
import {
  CreateDependencyActionPayload,
  deleteDependencyRelationsSagaAction,
  dependencyCreatedAction,
  UpdateSprintActionPayload,
  updateSprintSagaAction,
  UpdateTaskActionPayload,
  updateTaskSagaAction,
} from 'data/tasks/actions';
import { populateTaskWithNullDates } from 'data/tasks/sagas/create-dependency';
import { updateTasksDates } from 'data/tasks/slice';
import {
  getAlignPhaseDatesSetting,
  getNewDependenciesEnabledSetting,
  getQueryAdvancedSettingsConfigKey,
} from 'queries/advanced-settings/advanced-settings';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectSprintsQueryKey,
  getProjectTasksQueryKey,
} from 'queries/common';
import {
  DependencyEntity,
  Entity,
  EntityDateShiftUpdate,
  EntityType,
  getDateShiftsForEntity,
} from 'utils/date-shift';
import { getProjectDetailUrl } from 'utils/urls';

export const updateTaskDates = (
  projectId: number,
  updates: EntityDateShiftUpdate[],
  queryClient: QueryClient,
) => {
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));
  const tasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );

  if (project && tasks) {
    const updatedTasks = tasks.map((task) => {
      const update = updates.find((u) => u.id === task.taskId);

      if (update != null) {
        const { startDate, dueDate } = update;
        const _update = {} as Partial<Task>;

        if (startDate) {
          _update.startDate = moment(startDate).format(API_DATE_FORMAT);
        }
        if (dueDate) {
          _update.dueDate = moment(dueDate).format(API_DATE_FORMAT);
        }

        return {
          ...task,
          ..._update,
        };
      }

      return task;
    });

    const queryKey = getProjectTasksQueryKey(ProjectId(projectId));

    queryClient.setQueryData<Task[]>(queryKey, updatedTasks);
  }
};

export const updateProjectPhases = (
  projectId: number,
  updates: EntityDateShiftUpdate[],
  queryClient: QueryClient,
) => {
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));

  if (project) {
    const { projectPhases: phases } = project;
    const updatedPhases = phases.map((phase) => {
      const update = updates.find((u) => u.id === phase.projectPhaseId);

      if (update != null) {
        const { startDate, dueDate } = update;
        const _update = {} as Partial<ProjectPhase>;

        if (startDate) {
          _update.startDate = moment(startDate).format(API_DATE_FORMAT);
        }
        if (dueDate) {
          _update.dueDate = moment(dueDate).format(API_DATE_FORMAT);
        }

        return {
          ...phase,
          ..._update,
        };
      }

      return phase;
    });

    const queryKey = getProjectDetailQueryKey(ProjectId(projectId));

    queryClient.setQueryData<Project>(queryKey, () => ({
      ...project,
      projectPhases: updatedPhases,
    }));
  }
};

export const updateProjectSprints = (
  projectId: number,
  updates: EntityDateShiftUpdate[],
  queryClient: QueryClient,
) => {
  const sprintsData = queryClient.getQueryData<Sprint[]>(
    getProjectSprintsQueryKey(ProjectId(projectId)),
  );

  if (sprintsData) {
    const updatedSprints = sprintsData.map((sprint) => {
      const update = updates.find((u) => u.id === sprint.sprintId);

      if (update != null) {
        const { startDate, dueDate } = update;
        const _update = {} as Partial<ProjectPhase>;

        if (startDate) {
          _update.startDate = moment(startDate).format(API_DATE_FORMAT);
        }
        if (dueDate) {
          _update.dueDate = moment(dueDate).format(API_DATE_FORMAT);
        }

        return {
          ...sprint,
          ..._update,
        };
      }

      return sprint;
    });

    queryClient.setQueryData<Sprint[]>(
      getProjectSprintsQueryKey(ProjectId(projectId)),
      () => updatedSprints,
    );
  }
};

function* onUpdateTask({
  payload,
}: PayloadAction<UpdateTaskActionPayload>): Generator {
  const { taskId, projectId, data, options } = payload;
  const _projectId = ProjectId(projectId);

  /**
   * Patch fix to make date shifting work inside project detail
   * since phases and tasks for a project are pre-requisites for date shifting
   * which are available only inside project detail
   *
   * TODO: read the tasks data from query which is fetched on loading task pane - done by @partha
   * unlike redux store which is loaded only inside project detail route
   * Need a single source of truth.
   */

  if (!window.location.pathname.includes(getProjectDetailUrl(_projectId))) {
    return;
  }
  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(_projectId);
  const sprints = queryClient.getQueryData<Sprint[]>(
    getProjectSprintsQueryKey(_projectId),
  );
  const dependenciesMap = queryClient.getQueryData<DependenciesMap | undefined>(
    getProjectDependencyMapKey(projectId),
  );

  /**
   * Moved to react query!
   */
  const queryTasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );

  const ganttRef: any = yield getContext('ganttRef');

  if (!ganttRef) {
    return;
  }

  const { taskStore } = ganttRef.instance;

  if (!project || !queryTasks || !dependenciesMap) {
    return;
  }
  const { startDate, dueDate } = data;

  /**
   * dates will be set as null on clearing dates
   * hence processing null values.
   */
  if (!(startDate || startDate === null) && !(dueDate || dueDate === null)) {
    return;
  }
  const { projectPhases } = project;
  const tasks = normaliseTasks(queryTasks) as Task[];

  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);
  const sprintList = getSprintList(sprints ?? [], phaseList);

  const currentEntity = taskList[taskId];
  const taskRecord = tasks.find((_task) => _task.taskId === taskId);

  if (currentEntity == null) {
    return;
  }

  const updatedTask: Entity = {
    id: taskId,
    type: EntityType.Task,
    depth: currentEntity.depth,
  };

  if (startDate) {
    updatedTask.startDate = formatDate(startDate);
    if (!taskRecord?.dueDate && !dueDate) {
      updatedTask.dueDate = formatDate(startDate);
    }
  }
  if (dueDate) {
    updatedTask.dueDate = formatDate(dueDate);
    if (!taskRecord?.startDate && !startDate) {
      updatedTask.startDate = formatDate(dueDate);
    }
  }
  /** Handle clearing dates */
  if (startDate === null && dueDate === null) {
    const parentStartDate = formatDate(
      getParentStartDate(taskId, {
        ...phaseList,
        ...taskList,
      }),
    );

    updatedTask.startDate = parentStartDate;
    updatedTask.dueDate = parentStartDate;
  } else if (startDate === null) {
    updatedTask.startDate = formatDate(dueDate || taskList[taskId].dueDate);
  } else if (dueDate === null) {
    updatedTask.dueDate = formatDate(startDate || taskList[taskId].startDate);
  }

  const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
    getQueryAdvancedSettingsConfigKey(),
  );

  const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);
  const newDependenciesSettingEnabled =
    getNewDependenciesEnabledSetting(accountSettings);

  const [
    phaseUpdates,
    taskUpdates,
    sprintUpdates,
    _,
    dependencyRelationsToDelete,
  ] = getDateShiftsForEntity(phaseList, taskList, sprintList, updatedTask, {
    alignPhaseWithTask: alignPhaseDates,
    enabledNewDependencies: newDependenciesSettingEnabled,
    newTaskCreation: options?.newTaskCreation,
  });

  if (startDate || dueDate) {
    Object.values(taskUpdates).forEach((task) => {
      const _t = taskList[task.id];

      if (!_t?.hasStartDate || !_t?.hasDueDate) {
        populateTaskWithNullDates(
          projectId,
          task.id,
          taskList,
          Object.values(taskUpdates),
          taskStore,
          taskUpdates,
        );
      }
    });
  }

  yield put(
    updateGanttDatesAction({
      phaseUpdates,
      taskUpdates,
      phaseList,
      taskList,
      projectPhases,
      sprintUpdates,
      sprintList,
    }),
  );

  // if (startDate || dueDate) {
  //   const scrollToDate = startDate ? new Date(startDate) : new Date(dueDate);

  //   ganttRef?.instance.scrollToDate(scrollToDate);
  // }

  const filteredUpdates = filterTaskUpdates(taskUpdates, tasks);

  if (startDate) {
    filteredUpdates[taskId] = {
      ...filteredUpdates[taskId],
      startDate: taskUpdates[taskId].startDate,
    };
  }
  if (dueDate) {
    filteredUpdates[taskId] = {
      ...filteredUpdates[taskId],
      dueDate: taskUpdates[taskId].dueDate,
    };
  }
  if (!options?.skipStoreUpdate) {
    yield put(
      updateTasksDates(Object.values(filterTaskUpdates(taskUpdates, tasks))),
    );
  }

  updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
  updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
  updateTaskDates(projectId, Object.values(taskUpdates), queryClient);

  if (dependencyRelationsToDelete.length > 0) {
    yield put(
      deleteDependencyRelationsSagaAction({
        projectId,
        dependencyRelationsToDelete,
      }),
    );
  }
}

function* onUpdateSprint({
  payload,
}: PayloadAction<UpdateSprintActionPayload>): Generator {
  const { sprintId, projectId, data } = payload;
  const _projectId = ProjectId(projectId);

  if (!window.location.pathname.includes(getProjectDetailUrl(_projectId))) {
    return;
  }
  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(_projectId);
  const sprints =
    queryClient.getQueryData<Sprint[]>(getProjectSprintsQueryKey(_projectId)) ??
    [];
  const dependenciesMap = queryClient.getQueryData<DependenciesMap | undefined>(
    getProjectDependencyMapKey(projectId),
  );

  /**
   * Moved to react query!
   */
  const queryTasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );

  // const ganttRef = (yield getContext('ganttRef')) as any;

  if (!project || !queryTasks || !dependenciesMap) {
    return;
  }
  const { startDate, dueDate } = data;

  /**
   * dates will be set as null on clearing dates
   * hence processing null values.
   */
  if (!(startDate || startDate === null) && !(dueDate || dueDate === null)) {
    return;
  }
  const { projectPhases } = project;
  const tasks = normaliseTasks(queryTasks) as Task[];

  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);
  const sprintList = getSprintList(sprints, phaseList);

  const currentEntity = sprintList[sprintId];
  const taskRecord = sprints.find((_sprint) => _sprint.sprintId === sprintId);

  const updatedSprint: Entity = {
    id: sprintId,
    type: EntityType.Sprint,
    depth: currentEntity.depth,
  };

  if (startDate) {
    updatedSprint.startDate = formatDate(startDate);
    if (!taskRecord?.dueDate && !dueDate) {
      updatedSprint.dueDate = formatDate(startDate);
    }
  }
  if (dueDate) {
    updatedSprint.dueDate = formatDate(dueDate);
    if (!taskRecord?.startDate && !startDate) {
      updatedSprint.startDate = formatDate(dueDate);
    }
  }
  /** Handle clearing dates */
  if (startDate === null && dueDate === null) {
    const parentStartDate = formatDate(
      getParentStartDate(sprintId, {
        ...phaseList,
        ...sprintList,
      }),
    );

    updatedSprint.startDate = parentStartDate;
    updatedSprint.dueDate = parentStartDate;
  } else if (startDate === null) {
    updatedSprint.startDate = formatDate(
      dueDate || sprintList[sprintId].dueDate,
    );
  } else if (dueDate === null) {
    updatedSprint.dueDate = formatDate(
      startDate || sprintList[sprintId].startDate,
    );
  }

  const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
    getQueryAdvancedSettingsConfigKey(),
  );

  const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);
  const newDependenciesSettingEnabled =
    getNewDependenciesEnabledSetting(accountSettings);

  const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(
    phaseList,
    taskList,
    sprintList,
    updatedSprint,
    {
      alignPhaseWithTask: alignPhaseDates,
      enabledNewDependencies: newDependenciesSettingEnabled,
    },
  );

  yield put(
    updateGanttDatesAction({
      phaseUpdates,
      taskUpdates,
      phaseList,
      taskList,
      projectPhases,
      sprintUpdates,
      sprintList,
    }),
  );

  updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
  updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
  updateTaskDates(projectId, Object.values(taskUpdates), queryClient);
}

function* onUpdatePhase(
  action: StoreActionProps<ProjectPhaseDatesActionPayload>,
) {
  const { data } = action.payload;

  const { projectId, phaseId, startDate, dueDate } = data;

  const _projectId = ProjectId(projectId);

  if (!window.location.pathname.includes(getProjectDetailUrl(_projectId))) {
    return;
  }

  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(_projectId);
  const queryTasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );
  const sprints = queryClient.getQueryData<Sprint[]>(
    getProjectSprintsQueryKey(_projectId),
  );
  const dependenciesMap = queryClient.getQueryData<DependenciesMap | undefined>(
    getProjectDependencyMapKey(projectId),
  );

  if (!project || !queryTasks || !dependenciesMap) {
    return;
  }

  const { projectPhases } = project;
  const tasks = normaliseTasks(queryTasks) as Task[];

  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);
  const sprintList = getSprintList(sprints ?? [], phaseList);

  const currentEntity = phaseList[phaseId];

  const updatedPhase: Entity = {
    id: phaseId,
    type: EntityType.Phase,
    depth: currentEntity.depth,
  };
  const phaseRecord = projectPhases.find(
    (_phase) => _phase.projectPhaseId === phaseId,
  );

  if (startDate) {
    updatedPhase.startDate = formatDate(startDate);
    if (!phaseRecord?.dueDate && !dueDate) {
      updatedPhase.dueDate = formatDate(startDate);
    }
  }

  if (dueDate) {
    updatedPhase.dueDate = formatDate(dueDate);
    if (!phaseRecord?.startDate && !startDate) {
      updatedPhase.startDate = formatDate(dueDate);
    }
  }

  const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
    getQueryAdvancedSettingsConfigKey(),
  );

  const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);
  const newDependenciesSettingEnabled =
    getNewDependenciesEnabledSetting(accountSettings);

  const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(
    phaseList,
    taskList,
    sprintList,
    updatedPhase,
    {
      alignPhaseWithTask: alignPhaseDates,
      enabledNewDependencies: newDependenciesSettingEnabled,
    },
  );

  yield put(
    updateGanttDatesAction({
      phaseUpdates,
      taskUpdates,
      phaseList,
      taskList,
      projectPhases,
      sprintUpdates,
      sprintList,
    }),
  );

  // if (startDate || dueDate) {
  //   const scrollToDate = startDate ? new Date(startDate) : new Date(dueDate);

  //   ganttRef?.instance.scrollToDate(scrollToDate);
  // }
  updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
  updateTaskDates(projectId, Object.values(taskUpdates), queryClient);
  updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
}

function* onDependencyCreated({
  payload,
}: PayloadAction<CreateDependencyActionPayload>): Generator {
  const { taskId, projectId, dependentTaskId } = payload;
  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const queryTasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));
  const sprints = queryClient.getQueryData<Sprint[]>(
    getProjectSprintsQueryKey(ProjectId(projectId)),
  );
  const dependenciesMap = queryClient.getQueryData<DependenciesMap | undefined>(
    getProjectDependencyMapKey(projectId),
  );

  if (!project || !dependenciesMap) {
    return;
  }

  const { projectPhases } = project;
  const tasks = normaliseTasks(queryTasks) as Task[];
  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);
  const sprintList = getSprintList(sprints ?? [], phaseList);

  const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
    getQueryAdvancedSettingsConfigKey(),
  );

  const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);
  const newDependenciesSettingEnabled =
    getNewDependenciesEnabledSetting(accountSettings);

  const entityPayload = dependentTaskId
    ? {
        id: dependentTaskId,
        dependencyFor: [
          ...(taskList?.[dependentTaskId]?.dependencyFor || []),
          {
            id: taskId,
            dependencyType: 2,
            lag: null,
          },
        ],
        type: EntityType.Task,
        depth: taskList[dependentTaskId].depth,
      }
    : {
        id: taskId,
        dependentOn: [...(taskList?.[taskId]?.dependentOn || [])],
        type: EntityType.Task,
        depth: taskList[taskId].depth,
      };

  const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(
    phaseList,
    taskList,
    sprintList,
    entityPayload,
    {
      alignPhaseWithTask: alignPhaseDates,
      dependencyOperationsDone: true,
      enabledNewDependencies: newDependenciesSettingEnabled,
    },
  );

  yield put(
    updateGanttDatesAction({
      phaseUpdates,
      taskUpdates,
      phaseList,
      taskList,
      projectPhases,
      sprintUpdates,
      sprintList,
    }),
  );
  yield put(
    updateTasksDates(Object.values(filterTaskUpdates(taskUpdates, tasks))),
  );
  updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
  updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
  updateTaskDates(projectId, Object.values(taskUpdates), queryClient);
}

function* onPhaseDependencyCreated({
  payload,
}: PayloadAction<{
  projectPhaseId: number;
  projectId: number;
  predecessorDependencies?: DependencyEntity[];
}>): Generator {
  const { projectPhaseId, predecessorDependencies, projectId } = payload;
  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));
  const sprints = queryClient.getQueryData<Sprint[]>(
    getProjectSprintsQueryKey(ProjectId(projectId)),
  );
  const queryTasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );
  const tasks = normaliseTasks(queryTasks) as Task[];
  const dependenciesMap = queryClient.getQueryData<DependenciesMap | undefined>(
    getProjectDependencyMapKey(projectId),
  );

  if (!project || !dependenciesMap) {
    return;
  }

  const { projectPhases } = project;

  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);
  const sprintList = getSprintList(sprints ?? [], phaseList);

  const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
    getQueryAdvancedSettingsConfigKey(),
  );

  const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);
  const newDependenciesSettingEnabled =
    getNewDependenciesEnabledSetting(accountSettings);

  const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(
    phaseList,
    taskList,
    sprintList,
    {
      id: projectPhaseId,
      dependentOn: [
        ...(phaseList?.[projectPhaseId]?.dependentOn || []),
        ...(predecessorDependencies || []),
      ],
      type: EntityType.Phase,
      depth: phaseList[projectPhaseId].depth,
    },
    {
      alignPhaseWithTask: alignPhaseDates,
      dependencyOperationsDone: true,
      enabledNewDependencies: newDependenciesSettingEnabled,
    },
  );

  yield put(
    updateGanttDatesAction({
      phaseUpdates,
      taskUpdates,
      phaseList,
      taskList,
      projectPhases,
      sprintUpdates,
      sprintList,
    }),
  );
  yield put(
    updateTasksDates(Object.values(filterTaskUpdates(taskUpdates, tasks))),
  );

  updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
  updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
  updateTaskDates(projectId, Object.values(taskUpdates), queryClient);
}

export const watchDateShiftSaga = function* (): Generator {
  yield takeLatest(updateTaskSagaAction.type, onUpdateTask);
  yield takeLatest(updateSprintSagaAction.type, onUpdateSprint);
  yield takeLatest(dependencyCreatedAction.type, onDependencyCreated);
  yield takeLatest(updatePhaseDatesAction.type, onUpdatePhase);
  yield takeLatest(phaseDependencyCreated.type, onPhaseDependencyCreated);
};
